"use client";

import { useEffect, useRef, useState } from "react";

export default function Studio() {
  const videoRef = useRef(null);
  const programVideoRef = useRef(null);
  const [previewStream, setPreviewStream] = useState(null);
  const [programStream, setProgramStream] = useState(null);
  const [programOn, setProgramOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [previewSource, setPreviewSource] = useState("NONE");
  const [programSource, setProgramSource] = useState("NONE");
  const [error, setError] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [previewMuted, setPreviewMuted] = useState(false);
  const [previewVolume, setPreviewVolume] = useState(100);

  useEffect(() => {
    if (!previewStream || previewStream.getAudioTracks().length === 0) {
      setAudioLevel(0);
      return;
    }

    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    const audioContext = new AudioContextClass();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(previewStream);

    analyser.fftSize = 256;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    let animationFrame;

    function updateMeter() {
      analyser.getByteFrequencyData(data);

      let total = 0;
      for (let i = 0; i < data.length; i++) {
        total += data[i];
      }

      const average = total / data.length;
      const level = Math.min(100, Math.round(average * 1.5));

      setAudioLevel(level);
      animationFrame = requestAnimationFrame(updateMeter);
    }

    updateMeter();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      source.disconnect();
      analyser.disconnect();
      audioContext.close();
    };
  }, [previewStream]);

  function togglePreviewMute() {
    if (!previewStream) {
      setError("Select a Camera or Screen source first.");
      return;
    }

    const audioTracks = previewStream.getAudioTracks();

    if (audioTracks.length === 0) {
      setError("This source does not have an audio track.");
      return;
    }

    const newMuted = !previewMuted;

    audioTracks.forEach((track) => {
      track.enabled = !newMuted;
    });

    setPreviewMuted(newMuted);
    setError("");
  }

  function releasePreviewStream() {
    if (previewStream && previewStream !== programStream) {
      previewStream.getTracks().forEach((track) => track.stop());
    }
  }

  async function addCamera() {
    try {
      setError("");
      releasePreviewStream();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setPreviewStream(stream);
      setPreviewSource("CAMERA");
      setCameraOn(true);
    } catch (err) {
      console.error(err);
      setError("Camera or microphone access was denied or unavailable.");
    }
  }

  async function addScreen() {
    try {
      setError("");
      releasePreviewStream();

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setPreviewStream(stream);
      setPreviewSource("SCREEN");
      setCameraOn(true);

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.addEventListener("ended", () => {
          setCameraOn(false);
          setPreviewSource("NONE");
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        });
      }
    } catch (err) {
      console.error(err);

      if (err.name !== "NotAllowedError") {
        setError("Screen sharing was unavailable.");
      }
    }
  }

  function takeToProgram() {
    if (!previewStream) {
      setError("Select a Camera or Screen source first.");
      return;
    }

    if (
      programStream &&
      programStream !== previewStream
    ) {
      programStream.getTracks().forEach((track) => track.stop());
    }

    if (programVideoRef.current) {
      programVideoRef.current.srcObject = previewStream;
    }

    setProgramStream(previewStream);
    setProgramSource(previewSource);
    setProgramOn(true);
    setError("");
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background: "#0b0d10",
        color: "white",
        padding: "30px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h1 style={{ marginBottom: "5px" }}>PFM Studio Control Room</h1>
      <p style={{ color: "#aaa", marginTop: "0" }}>
        Patriots First Media Live Production
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginTop: "30px",
        }}
      >
        <section
          style={{
            background: "#191c21",
            padding: "20px",
            borderRadius: "10px",
          }}
        >
          <h2 style={{ color: "#35d04f" }}>
            PREVIEW <span style={{ color: "#aaa", fontSize: "16px" }}>— {previewSource}</span>
          </h2>

          <div
            style={{
              background: "#000",
              aspectRatio: "16/9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#777",
              overflow: "hidden",
              border: "3px solid #35d04f",
              borderRadius: "6px",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: cameraOn ? "block" : "none",
              }}
            />

            {!cameraOn && <span>Preview Monitor</span>}
          </div>
        </section>

        <section
          style={{
            background: "#191c21",
            padding: "20px",
            borderRadius: "10px",
          }}
        >
          <h2 style={{ color: "#ff3b3b" }}>
            PROGRAM <span style={{ color: "#aaa", fontSize: "16px" }}>— {programSource}</span>
          </h2>

          <div
            style={{
              background: "#000",
              aspectRatio: "16/9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#777",
              overflow: "hidden",
              border: "3px solid #ff3b3b",
              borderRadius: "6px",
            }}
          >
            <video
              ref={programVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: programOn ? "block" : "none",
              }}
            />

            {!programOn && <span>Live Program Monitor</span>}
          </div>
        </section>
      </div>

      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={takeToProgram}
          style={{
            padding: "14px 32px",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          TAKE → PROGRAM
        </button>
      </div>

      <div
        style={{
          marginTop: "20px",
          background: "#191c21",
          padding: "20px",
          borderRadius: "10px",
        }}
      >
        <h2>Audio Mixer</h2>

        <div style={{ marginBottom: "8px", color: "#aaa" }}>
          PREVIEW AUDIO — {previewSource}
        </div>

        <div
          style={{
            width: "100%",
            height: "24px",
            background: "#08090b",
            border: "1px solid #555",
            borderRadius: "5px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${audioLevel}%`,
              height: "100%",
              background: "#35d04f",
              transition: "width 0.05s linear",
            }}
          />
        </div>

        <div style={{ marginTop: "6px", color: "#aaa" }}>
          Level: {audioLevel}%
        </div>

        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
            }}
          >
            <span>VOLUME</span>
            <span>{previewVolume}%</span>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            value={previewVolume}
            onChange={(e) => setPreviewVolume(Number(e.target.value))}
            disabled={!previewStream}
            style={{
              width: "100%",
              cursor: previewStream ? "pointer" : "not-allowed",
            }}
          />
        </div>

        <button
          onClick={togglePreviewMute}
          disabled={!previewStream}
          style={{
            marginTop: "14px",
            padding: "10px 18px",
            fontWeight: "bold",
            cursor: previewStream ? "pointer" : "not-allowed",
            background: previewMuted ? "#d93030" : "#2a2e35",
            color: "#fff",
            border: previewMuted
              ? "2px solid #ff5555"
              : "2px solid #555",
            borderRadius: "6px",
            opacity: previewStream ? 1 : 0.5,
          }}
        >
          {previewMuted ? "🔇 MUTED — CLICK TO UNMUTE" : "🔊 MUTE"}
        </button>
      </div>

      <div
        style={{
          marginTop: "20px",
          background: "#191c21",
          padding: "20px",
          borderRadius: "10px",
        }}
      >
        <h2>Sources</h2>

        <button
          onClick={addCamera}
          style={{
            padding: "14px 22px",
            marginRight: "10px",
            fontWeight: "bold",
            cursor: "pointer",
            background: previewSource === "CAMERA" ? "#35d04f" : "#2a2e35",
            color: previewSource === "CAMERA" ? "#000" : "#fff",
            border: previewSource === "CAMERA"
              ? "2px solid #35d04f"
              : "2px solid #555",
            borderRadius: "6px",
          }}
        >
          📷 CAMERA
        </button>

        <button
          onClick={addScreen}
          style={{
            padding: "14px 22px",
            marginRight: "10px",
            fontWeight: "bold",
            cursor: "pointer",
            background: previewSource === "SCREEN" ? "#35d04f" : "#2a2e35",
            color: previewSource === "SCREEN" ? "#000" : "#fff",
            border: previewSource === "SCREEN"
              ? "2px solid #35d04f"
              : "2px solid #555",
            borderRadius: "6px",
          }}
        >
          🖥 SCREEN
        </button>

        <button
          disabled
          title="Guest source coming next"
          style={{
            padding: "14px 22px",
            fontWeight: "bold",
            background: "#2a2e35",
            color: "#777",
            border: "2px solid #444",
            borderRadius: "6px",
          }}
        >
          👤 GUEST
        </button>

        {error && (
          <p style={{ color: "#ff6b6b", marginTop: "15px" }}>
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
